import React from "react";
import { useTranslation } from "react-i18next";
import { LoaderCircle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { SubscriptionPlan } from "@/types/subscription-plan";
import { TenantSelectedPlanPreview } from "./TenantSelectedPlanPreview";

interface TenantAssignPlanSectionProps {
  availablePlans: SubscriptionPlan[];
  isAssigning: boolean;
  plansLoading: boolean;
  selectedPlan: SubscriptionPlan | null;
  selectedPlanId: string;
  onAssign: () => void;
  onSelectedPlanIdChange: (value: string) => void;
}

export const TenantAssignPlanSection = ({
  availablePlans,
  isAssigning,
  plansLoading,
  selectedPlan,
  selectedPlanId,
  onAssign,
  onSelectedPlanIdChange,
}: TenantAssignPlanSectionProps): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0 flex-1">
          <p className="mb-2 text-sm font-medium text-zinc-900">
            {t("subscriptions.tenantManagement.assign.label")}
          </p>
          <Select
            value={selectedPlanId}
            onValueChange={onSelectedPlanIdChange}
            disabled={plansLoading || isAssigning}
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={t(
                  "subscriptions.tenantManagement.assign.placeholder",
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {availablePlans.map((plan) => (
                <SelectItem key={plan.id} value={plan.id}>
                  {plan.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          variant="new_default"
          onClick={onAssign}
          disabled={
            isAssigning ||
            plansLoading ||
            availablePlans.length === 0 ||
            selectedPlanId.trim() === ""
          }
        >
          {isAssigning ? (
            <LoaderCircle className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          {t("subscriptions.tenantManagement.assign.action")}
        </Button>
      </div>

      {availablePlans.length === 0 && !plansLoading ? (
        <p className="mt-3 text-sm text-muted-foreground">
          {t("subscriptions.tenantManagement.assign.empty")}
        </p>
      ) : null}

      <TenantSelectedPlanPreview plan={selectedPlan} />
    </div>
  );
};
